import type { IndustryPack } from '../types'

export const INSURANCE_V1: IndustryPack = {
  pack_id: 'insurance_v1',
  industry: 'insurance',
  pack_name: 'Insurance Renewal & Claims',
  version: 1,
  rules: [
    {
      rule_id: 'INS_001_ambiguous_causation',
      severity: 'warn',
      match: { type: 'keyword_list', keywords: ['market conditions', 'industry-wide', 'general factors', 'economic environment'] },
      message: 'Vague causation language. Specify the actual driver of the premium change.',
      cited_authority: 'NAIC Model Bulletin on Premium Notice Clarity (Bulletin 1812, 2023)',
      suggested_rewrite_template: 'Replace with concrete drivers, e.g. "Your policy reflects [specific factor: zip-code loss data / vehicle type / claim history]."',
    },
    {
      rule_id: 'INS_003_il_naic_complaint_pattern',
      severity: 'warn',
      match: { type: 'keyword_list', keywords: ['unforeseen', 'beyond our control', 'unprecedented'] },
      message: 'Phrasing triggered 3 NAIC complaints in IL in 2024. State-specific litigation risk.',
      cited_authority: 'NAIC Complaint Database 2024-Q3 (Illinois)',
    },
    {
      rule_id: 'INS_004_claims_admission_language',
      severity: 'block',
      match: { type: 'keyword_list', keywords: ['we admit', 'we are at fault', 'our liability', 'we are liable', 'our fault'] },
      message: 'Admission language creates downstream litigation exposure. Replace with neutral status language.',
      cited_authority: 'AICPCU Best Practices Guide, §4.2',
      suggested_rewrite_template: 'Use "your claim is being reviewed" or "the investigation concluded that..."',
    },
    {
      rule_id: 'INS_006_renewal_increase_justification',
      severity: 'warn',
      match: { type: 'regex', pattern: '\\d+%\\s*(increase|higher|more)', flags: 'i' },
      message: 'Premium increase percentage present but justification may be absent. Ensure a reason is stated in the next sentence.',
      cited_authority: 'NAIC Model Regulation on Explanation of Premium Increases',
    },
    {
      rule_id: 'INS_007_deductible_clarity',
      severity: 'warn',
      match: { type: 'keyword_list', keywords: ['deductible', 'out-of-pocket'] },
      message: 'Deductible/OOP mentioned. Confirm the dollar amount appears within 50 words.',
      cited_authority: 'NAIC Principles-Based Reserving Working Group guidance',
    },
    {
      rule_id: 'INS_008_claims_status_timeliness',
      severity: 'info',
      match: { type: 'keyword_list', keywords: ['under review', 'pending', 'being processed', 'will contact you'] },
      message: 'Open-ended claims status language. Add an expected resolution timeframe per NAIC model standards.',
      cited_authority: 'NAIC Model Unfair Claims Settlement Practices Act, §6',
    },
    {
      rule_id: 'INS_009_aggressive_collections',
      severity: 'warn',
      match: { type: 'keyword_list', keywords: ['immediately pay', 'urgent action required', 'cancel without notice', 'sent to collections'] },
      message: 'Aggressive collection language may violate state-specific consumer protection rules.',
      cited_authority: 'CFPB UDAP guidance; state DOI bulletins 2023–2024',
    },
    {
      rule_id: 'INS_010_complaint_resolution_language',
      severity: 'info',
      match: { type: 'keyword_list', keywords: ['appeal', 'dispute', 'grievance', 'complaint'] },
      message: 'Complaint/appeal language present. Ensure the required state DOI complaint hotline is referenced.',
      cited_authority: 'Most state DOI regulations require complaint contact info in all adverse-decision letters',
    },
    {
      rule_id: 'INS_011_passive_aggressive_framing',
      severity: 'warn',
      match: { type: 'keyword_list', keywords: ['unfortunately', 'regret to inform', 'forced to', 'obligated to deny'] },
      message: 'Passive-aggressive framing detected. Focus group data shows 23% negative NPS impact.',
    },
    {
      rule_id: 'INS_012_readability',
      severity: 'warn',
      match: { type: 'readability', metric: 'flesch_kincaid', threshold: 10, operator: '>=' },
      message: 'Reading grade level is above 10. Insurance communications should target 8th grade or below.',
      cited_authority: 'Plain Writing Act (applies to federally-regulated insurers); state DOI plain language mandates',
      suggested_rewrite_template: 'Simplify sentence structure and replace technical terms with plain equivalents.',
    },
  ],
}
